import React, { useCallback, useEffect, useRef, useState } from "react";
import { ConversationProvider, useConversation } from "@elevenlabs/react";
import { ArrowUp, Loader2, Mic, MicOff, Phone, X } from "lucide-react";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "../elevenlabs/ui/conversation";
import type { AgentState, ElevenLabsSocketMessage } from "../elevenlabs/realtime-conversation-bar";
import LiveWaveform from "../elevenlabs/ui/live-waveform";
import ProfileImg from "../profile-img";
import useSyncConversationCustomer from "../../hooks/useSyncConversationCustomer";
import {
  rememberSessionToken,
  SessionEndedError,
  startChatSession,
} from "../../utils/elevenlabsSession";
import { ChatMessage, readChat, saveChat } from "../../utils/chatPersistence";
import DefaultProfile from "../../assets/images/default_profile_new.png";

type FormBypassConversationProps = {
  allocatedUserName?: string;
  avatarUrl: string;
  agentId?: string;
  customerId?: number | null;
  dynamicVariables?: Record<string, string | number | boolean>;
  agentVoiceId?: string;
  initialMode?: "voice" | "text";
  initialMessage?: string;
  onExit?: () => void;
  debug?: boolean;
  startTextOnly?: boolean;
  allowVoice?: boolean;
  onStart?: (message: string) => void;
  /** localStorage key of this website chat (see utils/chatPersistence) */
  chatKey?: string;
};

function safeError(err: unknown) {
  if (err instanceof Error) return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

function isElevenLabsSocketMessage(x: unknown): x is ElevenLabsSocketMessage {
  if (!x || typeof x !== "object") return false;
  const m = x as Record<string, unknown>;
  return (
    (m.source === "user" || m.source === "ai") && typeof m.message === "string"
  );
}

function stopMediaStream(stream: MediaStream | null) {
  if (!stream) return;
  stream.getTracks().forEach((t) => t.stop());
}

const FormBypassConversation = ({
  allocatedUserName,
  avatarUrl,
  agentId,
  customerId,
  dynamicVariables,
  agentVoiceId,
  initialMode = "text",
  initialMessage = "",
  onExit,
  debug = false,
  startTextOnly = false,
  allowVoice = true,
  onStart,
  chatKey,
}: FormBypassConversationProps) => {
  const debugLog = useCallback(
    (...args: unknown[]) => {
      if (!debug) return;
      // eslint-disable-next-line no-console
      console.log(`[FBC]`, ...args);
    },
    [debug],
  );

  // SingleForm writes/validates the record before mounting us; non-empty = restored chat.
  const [savedChat] = useState(() => (chatKey ? readChat(chatKey) : null));
  const resumed = Boolean(savedChat?.messages?.length);
  const [messages, setMessages] = useState<ChatMessage[]>(
    savedChat?.messages ?? [],
  );
  const [error, setError] = useState<string | null>(null);
  const [agentState, setAgentState] = useState<AgentState>("disconnected");
  const [localMode, setLocalMode] = useState<"voice" | "text">(
    allowVoice ? initialMode : "text",
  );
  const isMuted = localMode !== "voice";
  const [isTextSession, setIsTextSession] = useState(
    startTextOnly || !allowVoice,
  );
  const [voiceNotice, setVoiceNotice] = useState<string | null>(null);
  const [startRequested, setStartRequested] = useState(false);
  const switchingRef = useRef<"mic" | "swap" | null>(null);
  const unmountedRef = useRef(false);
  const [textInput, setTextInput] = useState("");
  const [sessionConversationId, setSessionConversationId] = useState<
    string | null
  >(null);
  const [micStream, setMicStream] = useState<MediaStream | null>(null);

  const mediaStreamRef = useRef<MediaStream | null>(null);
  const lastSentTextRef = useRef<string | null>(null);
  // restored chats wait for "Continue": we're mounted in the hidden popup on page load
  const didAutoStartRef = useRef(resumed);
  // startConversation can run after awaits (mic prompt, voice swap); it needs the latest transcript
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const didSendInitialMessageRef = useRef(false);
  const syncedConversationIdRef = useRef<string | null>(null);
  const attemptedSyncKeyRef = useRef<string | null>(null);
  // the SDK also reports a failed start to onError; startConversation handles (and retries) those
  const startingRef = useRef(false);

  const isConnected = agentState === "connected";
  // a disconnected chat with history (restored, or dropped) waits for "Continue";
  // not during the text->voice swap, which briefly disconnects on purpose
  const canContinue =
    agentState === "disconnected" && messages.length > 0 && !switchingRef.current;
  const canStartByMessage =
    Boolean(onStart) && !agentId && !initialMessage.trim() && !startRequested;
  const pendingFirstMessage = startTextOnly ? initialMessage.trim() : "";
  const shownMessages: ChatMessage[] =
    messages.length === 0 && pendingFirstMessage
      ? [{ role: "user", content: pendingFirstMessage }]
      : messages;
  const isStarting =
    startTextOnly &&
    !isConnected &&
    !canContinue &&
    Boolean(agentId || pendingFirstMessage || startRequested);
  const profileSrc = avatarUrl || DefaultProfile;

  const { syncConversationCustomer } = useSyncConversationCustomer();

  const conversation = useConversation({
    textOnly: false,
    // a websocket text session has no audio: 1.x throws on setMicMuted/setVolume there
    ...(isTextSession
      ? {}
      : { micMuted: isMuted, volume: localMode === "text" ? 0 : 1 }),
    onConnect: () => {
      debugLog("onConnect()");
      setError(null);
    },
    onDisconnect: () => {
      debugLog("onDisconnect()");
      setAgentState("disconnected");
      setSessionConversationId(null);
      syncedConversationIdRef.current = null;
      attemptedSyncKeyRef.current = null;
      if (switchingRef.current === "swap" || unmountedRef.current) return;
      // keep the transcript so the visitor can continue; End calls onExit itself
      if (!messagesRef.current.length) onExit?.();
    },
    onMessage: (message: unknown) => {
      debugLog("onMessage()", message);
      if (!isElevenLabsSocketMessage(message)) return;
      const { source, message: text } = message;
      if (source === "user" && text === lastSentTextRef.current) {
        lastSentTextRef.current = null;
        return;
      }
      setMessages((prev) => [
        ...prev,
        { role: source === "user" ? "user" : "assistant", content: text },
      ]);
    },
    onConversationMetadata: rememberSessionToken(chatKey),
    onError: (err: unknown) => {
      if (startingRef.current) return;
      debugLog("onError()", err);
      setAgentState("disconnected");
      setError(safeError(err));
    },
  });

  const syncCustomerConversation = useCallback(
    async (conversationIdFromStart?: string | null) => {
      if (customerId == null) return;
      const conversationId = conversationIdFromStart;
      if (!conversationId) return;
      const syncKey = `${customerId}:${conversationId}`;
      if (attemptedSyncKeyRef.current === syncKey) return;
      if (syncedConversationIdRef.current === conversationId) return;
      // read now: in skip-intro mode the record is written after we mount
      const chatId = chatKey ? readChat(chatKey)?.chatId : undefined;
      try {
        attemptedSyncKeyRef.current = syncKey;
        await syncConversationCustomer({
          customer_id: customerId,
          conversation_id: conversationId,
          ...(chatId && { chat_id: chatId }),
        });
        syncedConversationIdRef.current = conversationId;
      } catch {}
    },
    [customerId, chatKey, syncConversationCustomer],
  );

  useEffect(() => {
    if (chatKey && messages.length) saveChat(chatKey, { messages });
  }, [chatKey, messages]);

  useEffect(() => {
    if (!sessionConversationId) return;
    if (syncedConversationIdRef.current === sessionConversationId) return;
    void syncCustomerConversation(sessionConversationId);
  }, [sessionConversationId, syncCustomerConversation]);

  const getMicStream = useCallback(async () => {
    if (mediaStreamRef.current) return mediaStreamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;
    setMicStream(stream);
    return stream;
  }, []);

  const releaseMic = useCallback(() => {
    stopMediaStream(mediaStreamRef.current);
    mediaStreamRef.current = null;
    setMicStream(null);
  }, []);

  const startConversation = useCallback(async (textOnly: boolean): Promise<boolean> => {
    if (!agentId) return false;
    try {
      setAgentState("connecting");
      if (!textOnly) await getMicStream();
      if (unmountedRef.current) {
        releaseMic();
        return false;
      }
      startingRef.current = true;
      const startedId = await startChatSession(conversation, {
        agentId,
        voice: !textOnly,
        textOnly,
        chatKey,
        history: messagesRef.current,
        voiceId: agentVoiceId,
        dynamicVariables,
        onStatusChange: (status: unknown) => {
          if (status && typeof status === "object" && "status" in status) {
            const next = (status as { status?: AgentState }).status ?? null;
            setAgentState(next);
          }
        },
      });
      if (unmountedRef.current) {
        conversation.endSession();
        return false;
      }
      setSessionConversationId(startedId || null);
      return true;
    } catch (err) {
      debugLog("startConversation(): failed", err);
      setAgentState("disconnected");
      if (!(err instanceof SessionEndedError)) setError(safeError(err));
      return false;
    } finally {
      startingRef.current = false;
    }
  }, [agentId, chatKey, conversation, debugLog, dynamicVariables, agentVoiceId, getMicStream, releaseMic]);

  const handleEndSession = useCallback(() => {
    conversation.endSession();
    setAgentState("disconnected");
    setSessionConversationId(null);
    syncedConversationIdRef.current = null;
    attemptedSyncKeyRef.current = null;
    releaseMic();
    onExit?.();
  }, [conversation, onExit, releaseMic]);

  // auto-start once agentId is available
  useEffect(() => {
    if (didAutoStartRef.current) return;
    if (!agentId) return;
    if (agentState !== "disconnected") return;

    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled || !agentId || agentState !== "disconnected") return;
      didAutoStartRef.current = true;
      startConversation(isTextSession);
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [agentId, agentState, isTextSession, startConversation]);

  // cleanup on unmount
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      stopMediaStream(mediaStreamRef.current);
    };
  }, []);

  const handlePhoneToggle = useCallback(async () => {
    if (!allowVoice || switchingRef.current) return;
    if (!isTextSession) {
      setLocalMode((prev) => (prev === "text" ? "voice" : "text"));
      return;
    }
    if (!isConnected && !canStartByMessage) return;
    switchingRef.current = "mic";
    try {
      const micGranted = await getMicStream().then(
        () => true,
        () => false,
      );
      if (!micGranted) {
        setVoiceNotice("Microphone is blocked. You can keep chatting here.");
        return;
      }
      setVoiceNotice(null);
      if (canStartByMessage) {
        setStartRequested(true);
        setIsTextSession(false);
        setLocalMode("voice");
        onStart?.("");
        return;
      }
      switchingRef.current = "swap";
      await conversation.endSession();
      // voice can't resume the text conversation; it starts a new one with the transcript replayed
      if (chatKey) saveChat(chatKey, { sessionToken: undefined });
      setIsTextSession(false);
      setLocalMode("voice");
      let started = await startConversation(false);
      if (!started) {
        releaseMic();
        setIsTextSession(true);
        setLocalMode("text");
        started = await startConversation(true);
        if (started) {
          setVoiceNotice("Couldn't start voice. You can keep chatting here.");
        }
      }
    } finally {
      switchingRef.current = null;
    }
  }, [
    allowVoice,
    canStartByMessage,
    chatKey,
    conversation,
    getMicStream,
    isConnected,
    isTextSession,
    onStart,
    releaseMic,
    startConversation,
  ]);

  const handleSendText = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!textInput.trim()) return;
      if (canStartByMessage) {
        setStartRequested(true);
        onStart?.(textInput.trim());
        setTextInput("");
        return;
      }
      if (!isConnected) return;
      const msg = textInput.trim();
      setVoiceNotice(null);
      lastSentTextRef.current = msg;
      setMessages((prev) => [...prev, { role: "user", content: msg }]);
      conversation.sendUserMessage(msg);
      setTextInput("");
    },
    [conversation, textInput, isConnected, canStartByMessage, onStart],
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTextInput(e.target.value);
      if (isConnected) conversation.sendUserActivity?.();
    },
    [conversation, isConnected],
  );

  useEffect(() => {
    const message = initialMessage.trim();
    if (!isConnected || !message || didSendInitialMessageRef.current) return;

    didSendInitialMessageRef.current = true;
    lastSentTextRef.current = message;
    setMessages((prev) => [...prev, { role: "user", content: message }]);
    conversation.sendUserMessage(message);
  }, [conversation, initialMessage, isConnected]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendText();
      }
    },
    [handleSendText],
  );

  if (allowVoice && localMode === "voice") {
    return (
      <div className="flex flex-col items-center flex-1 w-full">
        <ProfileImg avatar={profileSrc} />
        <p className="text-lg font-semibold text-center leading-6 m-0">
          Hi this is {allocatedUserName}. How can I help you today?
        </p>
        <div className="flex flex-col items-center justify-center gap-2 w-full my-4">
          <LiveWaveform
            active={isConnected}
            idlePreview={!isConnected}
            stream={micStream ?? undefined}
            height={40}
            barWidth={4}
            barGap={4}
            barRadius={999}
            fadeEdges={false}
          />
        </div>
        {canContinue ? (
          <button
            type="button"
            className="w-10 h-10 rounded-full bg-secondary border-0 shadow-none text-white flex items-center justify-center cursor-pointer shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ width: "auto", padding: "0 16px" }}
            onClick={() => startConversation(isTextSession)}
          >
            Continue conversation
          </button>
        ) : (
          <p className="text-sm text-gray-500 m-0">
            {agentState === "connected"
              ? "Listening..."
              : agentState === "connecting" || (startRequested && !agentId)
              ? "Connecting..."
              : ""}
          </p>
        )}
        <div className="flex flex-row justify-center items-center gap-4 my-2">
          <button
            type="button"
            className="w-10 h-10 rounded-full border border-[#d7d7d7] bg-white shadow-none text-[#1c1d21] flex items-center justify-center cursor-pointer"
            onClick={() => setLocalMode((prev) => (prev === "voice" ? "text" : "voice"))}
            aria-label={isMuted ? "Unmute" : "Mute"}
            aria-pressed={!isMuted}
          >
            {isMuted ? (
              <MicOff className="w-6 h-6" aria-hidden="true" />
            ) : (
              <Mic className="w-6 h-6" aria-hidden="true" />
            )}
          </button>
          {agentId && (
            <button
              type="button"
              className="w-10 h-10 rounded-full bg-red-500 border-0 shadow-none text-white flex items-center justify-center cursor-pointer"
              onClick={handleEndSession}
              aria-label="End call"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          )}
        </div>
        <form
          className="w-full mt-4"
          style={{ marginTop: "auto" }}
          onSubmit={handleSendText}
        >
          <div className="flex flex-row items-center gap-2 w-full mt-2">
            <input
              className="flex-1 border border-[#d7d7d7] rounded-full py-2.5 px-4 text-sm outline-none box-border bg-white text-[#1c1d21]"
              placeholder="Or type here to chat"
              value={textInput}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              disabled={!isConnected}
            />
            <button
              type="submit"
              className="w-10 h-10 rounded-full bg-secondary border-0 shadow-none text-white flex items-center justify-center cursor-pointer shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
              aria-label="Send"
              disabled={!textInput.trim() || !isConnected}
            >
              <ArrowUp className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-row items-center justify-between gap-3 py-3 border-b border-[#f0f0f0] shrink-0" style={{ flexShrink: 0 }}>
        <div className="flex flex-row items-center gap-2 flex-1">
          <ProfileImg avatar={profileSrc} small />
          <span className="text-sm font-medium text-[#1c1d21] flex-1 leading-5">
            Hi this is {allocatedUserName}. How can I help you today?
          </span>
        </div>
        {allowVoice && (
          <button
            type="button"
            className="w-9 h-9 rounded-full bg-secondary border-0 shadow-none text-white flex items-center justify-center cursor-pointer shrink-0"
            onClick={handlePhoneToggle}
            aria-label="Switch to voice"
          >
            <Phone className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="relative flex-1" style={{ overflow: "hidden" }}>
        <Conversation className="absolute inset-0 pb-20">
          <ConversationContent className="flex min-w-0 flex-col gap-1.5 pb-6 p-0">
            {error ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            ) : shownMessages.length === 0 && !isStarting ? (
              <div className="text-sm text-muted-foreground text-center mt-4">
                Start a conversation
              </div>
            ) : (
              <>
              {shownMessages.map((message, index) => {
                const isUser = message.role === "user";
                const prevRole = shownMessages[index - 1]?.role;
                const showLabel = index === 0 || prevRole !== message.role;
                return (
                  <div
                    key={index}
                    className={`flex w-full flex-col ${isUser ? "items-end" : "items-start"}`}
                  >
                    {showLabel && (
                      <div className="mb-1 text-xs text-muted-foreground flex items-center">
                        {!isUser && (
                          <img
                            src={profileSrc}
                            alt="agent"
                            className="h-5 w-5 rounded-full mr-1"
                          />
                        )}
                        <span>
                          {isUser ? "You" : (allocatedUserName ?? "Agent")}
                        </span>
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] py-2 px-4 rounded-xl text-sm leading-[1.375rem] whitespace-pre-wrap [overflow-wrap:anywhere] ${
                        isUser
                          ? "bg-secondary text-white"
                          : "bg-slate-200 text-[#1c1d21]"
                      }`}
                    >
                      {message.content}
                    </div>
                  </div>
                );
              })}
              {isStarting && (
                <Loader2 className="w-5 h-5 animate-spin mx-auto mt-4" />
              )}
              </>
            )}
          </ConversationContent>
          <ConversationScrollButton className="bottom-30" />
        </Conversation>

        {/* Bottom bar */}
        <div className="absolute bottom-0 left-0 right-0">
          <form className="w-full mt-4" onSubmit={handleSendText}>
            {voiceNotice && (
              <div className="mb-1 text-center text-xs" style={{ color: "#DC2626" }}>
                {voiceNotice}
              </div>
            )}
            {canContinue ? (
              <button
                type="button"
                className="w-10 h-10 rounded-full bg-secondary border-0 shadow-none text-white flex items-center justify-center cursor-pointer shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ width: "100%" }}
                onClick={() => startConversation(isTextSession)}
              >
                Continue chat
              </button>
            ) : (
              <div className="flex flex-row items-center gap-2 w-full mt-2">
                <input
                  className="flex-1 border border-[#d7d7d7] rounded-full py-2.5 px-4 text-sm outline-none box-border bg-white text-[#1c1d21]"
                  placeholder={
                    isConnected || canStartByMessage
                      ? "Type your message..."
                      : "Connecting..."
                  }
                  disabled={!isConnected && !canStartByMessage}
                  value={textInput}
                  onChange={handleTextChange}
                  onKeyDown={handleKeyDown}
                />
                <button
                  type="submit"
                  className="w-10 h-10 rounded-full bg-secondary border-0 shadow-none text-white flex items-center justify-center cursor-pointer shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                  aria-label="Send"
                  disabled={!textInput.trim() || (!isConnected && !canStartByMessage)}
                >
                  <ArrowUp className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            )}
            {agentId && (
              <button
                type="button"
                className="block mx-auto mt-2 bg-transparent border-0 text-gray-400 text-sm underline cursor-pointer"
                onClick={handleEndSession}
              >
                End Chat
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

// @elevenlabs/react 1.x keeps the session in a provider
const FormBypassConversationWithProvider = (props: FormBypassConversationProps) => (
  <ConversationProvider>
    <FormBypassConversation {...props} />
  </ConversationProvider>
);

export default FormBypassConversationWithProvider;
